import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, TextField, Grid, FormControl,
    InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Button, Divider, Tooltip
} from '@mui/material';
import { FiPlus, FiTrash2, FiX, FiRefreshCw } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { getSession } from '../../../../getSession';
import { saveActivityMaster, getActivityDetailById } from '../../../../utils/lead';
import Swal from 'sweetalert2';

const CreateEventForm = ({ onSuccess }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { userId, parentId } = getSession();

    const [formData, setFormData] = useState({
        EventId: 0,
        EventName: `Event_${new Date().getTime()}`,
        EntityName: 'Lead',
        HasProduct: false,
        EventDescription: '',
        WebhookProperty: [
            {
                KeyName: 'EventIdentifier', DisplayName: 'Event Identifier',
                DataType: 'string', IsRequired: true, DefaultValue: `Event_${new Date().getTime()}`, IsRemovable: false
            },
            {
                KeyName: 'EntityIdentifier', DisplayName: 'Entity Identifier',
                DataType: 'string', IsRequired: true, DefaultValue: `MobileNo`, IsRemovable: false
            },
            {
                KeyName: 'MobileNo', DisplayName: 'MobileNo',
                DataType: 'string', IsRequired: true, DefaultValue: `#MobileNo#`, IsRemovable: false
            }
        ]
    });

    const [newProp, setNewProp] = useState({
        KeyName: '',
        DisplayName: '',
        DataType: '',
        IsRequired: false,
        DefaultValue: ''
    });

    useEffect(() => {
        if (id) {
            fetchEventDetails(id);
        }
    }, [id]);

    const fetchEventDetails = async (eventId) => {
        try {
            const { TokenId } = getSession();
            const payload = {
                Token: TokenId,
                Details: {
                    EventId: eventId,
                    UserId: userId
                }
            };
            const response = await getActivityDetailById(payload);
            const result = response.d || response;

            if (result && result.Status === 1) {
                const data = result.Details;
                if (data && data.UiSettingJSON) {
                    const ui = JSON.parse(data.UiSettingJSON);
                    setFormData(ui);
                }
            }
        } catch (error) {
            console.error('Error fetching event details:', error);
        }
    };

    const handleAddProperty = () => {
        if (!newProp.KeyName || !newProp.DisplayName) {
            Swal.fire('Warning', 'Key Name and Display Name are required', 'warning');
            return;
        }

        setFormData(prev => ({
            ...prev,
            WebhookProperty: [...prev.WebhookProperty, { ...newProp, IsRemovable: true }]
        }));

        setNewProp({
            KeyName: '',
            DisplayName: '',
            DataType: '',
            IsRequired: false,
            DefaultValue: ''
        });
    };

    const handleRemoveProperty = (index) => {
        setFormData(prev => ({
            ...prev,
            WebhookProperty: prev.WebhookProperty.filter((_, i) => i !== index)
        }));
    };

    const handleSave = async () => {
        if (!formData.EventName) {
            Swal.fire('Warning', 'Event Name is required', 'warning');
            return;
        }

        if (!/^[a-zA-Z0-9_ ]+$/.test(formData.EventName)) {
            Swal.fire('Warning', 'Special characters are not allowed in Event Name', 'warning');
            return;
        }

        try {
            const { TokenId } = getSession();
            const payload = {
                Token: TokenId,
                Details: {
                    EventId: formData.EventId || 0,
                    EventName: formData.EventName,
                    EventTemplate: formData.EventDescription,
                    EventEntity: formData.EntityName,
                    ParentId: parentId,
                    UserId: userId,
                    LeadSettingJSON: JSON.stringify({}),
                    UiSettingJSON: JSON.stringify(formData)
                }
            };

            const response = await saveActivityMaster(payload);
            const result = response.d || response;

            if (result && result.Status === 1) {
                const details = result.Details;
                let isSuccess = false;

                switch (details?.toLowerCase()) {
                    case 'inserted':
                    case 'updated':
                        isSuccess = true;
                        break;
                    case 'exists':
                        Swal.fire('OOPs...', 'Event Name already exists', 'warning');
                        break;
                    default:
                        if (details === '1' || details === 1) isSuccess = true;
                        break;
                }

                if (isSuccess) {
                    Swal.fire('Success', 'Event saved successfully', 'success');
                    if (onSuccess) onSuccess();
                    else navigate('/custom-events/webhook');
                } else if (details?.toLowerCase() !== 'exists') {
                    Swal.fire('OOPs...', result.Message || 'Unable to save event', 'warning');
                }
            } else {
                Swal.fire('Error', response?.Message || 'Failed to save event', 'error');
            }
        } catch (error) {
            console.error('Error saving event:', error);
            Swal.fire('Error', 'Failed to save event', 'error');
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#eeeeee' }}>
            {/* Header as per Image 2 */}
            <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eeeeee', borderBottom: '1px solid #ddd' }}>
                <IconButton size="small" onClick={() => navigate('/custom-events/webhook')}>
                    <FiX />
                </IconButton>
                <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 500, color: '#333' }}>
                    Back to Event List
                </Typography>
            </Box>

            <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
                <Paper sx={{ p: 0, borderRadius: '4px', overflow: 'hidden' }}>
                    {/* Event Details Section */}
                    <Box sx={{ bgcolor: '#f8f9fa', px: 3, py: 1, borderBottom: '1px solid #eee' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>Event Details</Typography>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: '#334155' }}>Event Name</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={formData.EventName}
                                    onChange={(e) => setFormData({ ...formData, EventName: e.target.value })}
                                    sx={{ bgcolor: 'white' }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: '#334155' }}>Event Entity</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={formData.EntityName}
                                        onChange={(e) => setFormData({ ...formData, EntityName: e.target.value })}
                                        sx={{ bgcolor: 'white' }}
                                    >
                                        <MenuItem value="Lead">Lead</MenuItem>
                                        <MenuItem value="Enquiry">Enquiry</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#334155' }}>Webhook Property Definition</Typography>
                                <IconButton size="small" sx={{ bgcolor: '#14b8a6', color: 'white', '&:hover': { bgcolor: '#0d9488' }, borderRadius: '4px' }}>
                                    <FiRefreshCw size={14} />
                                </IconButton>
                            </Box>

                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '4px' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'white' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, color: '#334155', borderRight: '1px solid #eee' }}>KeyName</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#334155', borderRight: '1px solid #eee' }}>Display Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#334155', borderRight: '1px solid #eee' }}>Data type</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#334155', borderRight: '1px solid #eee' }}>Is Required</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#334155' }}>Default Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.WebhookProperty.map((prop, index) => (
                                            <TableRow key={index} sx={{ bgcolor: prop.IsRemovable ? 'white' : '#f8f9fa' }}>
                                                <TableCell sx={{ borderRight: '1px solid #eee' }}>{prop.KeyName}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #eee' }}>{prop.DisplayName}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #eee' }}>{prop.DataType}</TableCell>
                                                <TableCell sx={{ borderRight: '1px solid #eee' }}>{prop.IsRequired ? 'true' : 'false'}</TableCell>
                                                <TableCell sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={prop.DefaultValue}
                                                        disabled
                                                        sx={{ bgcolor: '#f1f5f9', '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                                                    />
                                                    {prop.IsRemovable && (
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveProperty(index)}>
                                                            <FiTrash2 size={14} />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {/* New Property Footer Row as per Image 2 */}
                                        <TableRow>
                                            <TableCell sx={{ borderRight: '1px solid #eee' }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={newProp.KeyName}
                                                    onChange={(e) => setNewProp({ ...newProp, KeyName: e.target.value })}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ borderRight: '1px solid #eee' }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={newProp.DisplayName}
                                                    onChange={(e) => setNewProp({ ...newProp, DisplayName: e.target.value })}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ borderRight: '1px solid #eee' }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={newProp.DataType}
                                                        onChange={(e) => setNewProp({ ...newProp, DataType: e.target.value })}
                                                        displayEmpty
                                                    >
                                                        <MenuItem value="" disabled>Select DataType</MenuItem>
                                                        <MenuItem value="string">string</MenuItem>
                                                        <MenuItem value="numeric">numeric</MenuItem>
                                                        <MenuItem value="date">date</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell sx={{ borderRight: '1px solid #eee', textAlign: 'center' }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={newProp.IsRequired}
                                                    onChange={(e) => setNewProp({ ...newProp, IsRequired: e.target.checked })}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={newProp.DefaultValue}
                                                    onChange={(e) => setNewProp({ ...newProp, DefaultValue: e.target.value })}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleAddProperty}
                                                    sx={{ borderColor: '#14b8a6', color: '#14b8a6', '&:hover': { borderColor: '#0d9488', bgcolor: '#f0fdfa' } }}
                                                >
                                                    Add
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        <Box sx={{ mt: 4 }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: '#334155' }}>Event Description</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Please enter Event Description"
                                value={formData.EventDescription}
                                onChange={(e) => setFormData({ ...formData, EventDescription: e.target.value })}
                            />
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={formData.HasProduct}
                                        onChange={(e) => setFormData({ ...formData, HasProduct: e.target.checked })}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ color: '#334155' }}>Include product in payload</Typography>}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Footer Actions as per Image 2 */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', bgcolor: '#f8f9fa', borderTop: '1px solid #eee' }}>
                <Button variant="text" sx={{ color: '#666', textTransform: 'none' }} onClick={() => navigate('/custom-events/webhook')}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, textTransform: 'none', px: 4 }}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
};

export default CreateEventForm;
